import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, API, getToken } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { DownloadSimple, Trash, Database, ArrowClockwise } from "@phosphor-icons/react";

function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function BackupPage() {
  useDocumentTitle("Backup");
  const [items, setItems] = useState([]);
  const [retention, setRetention] = useState(30);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/backups");
      setItems(data.backups);
      setRetention(data.retention_days);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createNow = async () => {
    setCreating(true);
    try {
      await api.post("/admin/backup/create");
      toast.success("Backup creato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const download = async (filename) => {
    try {
      const url = filename
        ? `${API}/admin/backup/download?filename=${encodeURIComponent(filename)}`
        : `${API}/admin/backup/download`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error("Errore download");
      const blob = await res.blob();
      const objUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename || `backup-${new Date().toISOString().replace(/[:.]/g, "-")}.json.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objUrl);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (filename) => {
    if (!window.confirm(`Eliminare il backup ${filename}?`)) return;
    try {
      await api.delete(`/admin/backup/${filename}`);
      toast.success("Backup eliminato");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// AMMINISTRAZIONE</div>
          <h1 className="text-4xl font-display font-bold tracking-tight">Backup del database</h1>
          <p className="text-muted-foreground mt-2">
            Backup automatico ogni giorno alle 03:00 UTC. Retention: {retention} giorni.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="backup-refresh" className="rounded-full gap-2">
            <ArrowClockwise size={16} /> Aggiorna
          </Button>
          <Button onClick={createNow} disabled={creating} data-testid="backup-create-now" className="rounded-full gap-2">
            <Database size={16} weight="bold" /> {creating ? "Creo..." : "Crea backup ora"}
          </Button>
          <Button onClick={() => download(null)} variant="outline" data-testid="backup-download-fresh" className="rounded-full gap-2">
            <DownloadSimple size={16} /> Scarica ora (fresco)
          </Button>
        </div>
      </header>

      <Card className="border-border/70 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/60">
              <tr className="text-left">
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">File</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Dimensione</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Data</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Caricamento...</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-12 text-center">
                  <Database size={32} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
                  <div className="text-muted-foreground">Nessun backup ancora. Cliccane "Crea backup ora" per iniziare.</div>
                </td></tr>
              )}
              {items.map((b) => (
                <tr key={b.filename} className="border-t border-border/50" data-testid={`backup-row-${b.filename}`}>
                  <td className="px-4 py-3 font-mono text-xs">{b.filename}</td>
                  <td className="px-4 py-3">
                    <Badge className="bg-secondary text-foreground border border-border font-mono text-[10px]">{fmtSize(b.size)}</Badge>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                    {new Date(b.created_at).toLocaleString("it-IT")}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => download(b.filename)} data-testid={`backup-download-${b.filename}`} className="gap-1">
                        <DownloadSimple size={14} /> Scarica
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(b.filename)} className="text-destructive hover:text-destructive" data-testid={`backup-delete-${b.filename}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-6 text-xs text-muted-foreground">
        Ogni backup contiene tutte le collezioni: <span className="font-mono">users, customers, interventions</span>. Formato: JSON compresso gzip.
      </div>
    </div>
  );
}
